import React from 'react';
import { AlertTriangle, X, RefreshCw } from 'lucide-react';

interface SuspendedUserPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onResubmit: () => void;
  reason?: string;
}

const SuspendedUserPopup: React.FC<SuspendedUserPopupProps> = ({
  isOpen,
  onClose,
  onResubmit,
  reason
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full border border-gray-700 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="bg-orange-500/20 p-2 rounded-full">
              <AlertTriangle className="h-6 w-6 text-orange-500" />
            </div>
            <h2 className="text-xl font-semibold text-white">Account Suspended</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Message */}
        <div className="mb-6">
          <p className="text-gray-300 mb-3">
            Your account has been suspended due to one of the following reasons:
          </p>
          <ul className="text-gray-300 text-sm space-y-2 mb-4">
            <li className="flex items-start space-x-2">
              <span className="text-orange-400 mt-1">•</span>
              <span>KYC verification not fulfilled</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-orange-400 mt-1">•</span>
              <span>Unauthorized use or suspicious activity</span>
            </li>
            <li className="flex items-start space-x-2">
              <span className="text-orange-400 mt-1">•</span>
              <span>Too many failed login attempts</span>
            </li>
          </ul>
          
          {reason && (
            <div className="bg-gray-700/50 p-3 rounded border border-gray-600">
              <p className="text-sm text-gray-300">
                <span className="font-medium text-orange-400">Specific reason:</span> {reason}
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors duration-200 font-medium"
          >
            OK, I understand
          </button>
          <button
            onClick={onResubmit}
            className="flex-1 px-4 py-2 bg-[#2AB7A6] hover:bg-[#25A395] text-white rounded-lg transition-colors duration-200 font-medium flex items-center justify-center space-x-2"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Resubmit Info</span>
          </button>
        </div>

        {/* Additional Info */}
        <div className="mt-4 text-center">
          <p className="text-xs text-gray-500">
            Need help? Contact our support team
          </p>
        </div>
      </div>
    </div>
  );
};

export default SuspendedUserPopup;
